<template>
  <div class="w-full max-w-xs sm:max-w-md mx-auto py-2 px-4 rounded-lg">
    <div class="bg-green-500 text-white px-4 py-2 rounded-t-md text-center font-bold text-xl">
      Get a Free Property Cash Offer
    </div>
    <div class="bg-green-500 text-white px-4 py-1 text-center font-semibold text-sm">
      No-Obligation - Instant Decision<br>A Guaranteed Purchase On Any Property
    </div>
    <form @submit.prevent="handleSubmit" class="bg-white shadow-md rounded-b-md px-6 sm:px-8 pt-6 pb-4">
      <!-- STEP 1: Postcode Entry with Autocomplete -->
      <div class="mb-4" v-if="!addressSelected">
        <label class="block text-gray-700 font-bold mb-2 text-lg flex justify-between items-center" for="postcode">
          <div class="flex items-center">
            Get Started...
            <span class="tooltip group relative flex items-center ml-2">
              <i class="fas fa-info-circle text-gray-400 hover:text-gray-600 cursor-pointer"></i>
              <span class="tooltip-text group-hover:block absolute bg-gray-700 text-white text-xs rounded p-2 bottom-full mb-2 hidden -translate-x-1/2 transform w-64 sm:w-48">
                We aim to give you the best cash offer quickly and efficiently. Keep in mind, our offer will be below market value. If the initial offer doesn't meet your expectations, we have other options available.
              </span>
            </span>
        </div>
        </label>
        
        <!-- Postcode Input -->
        <div class="flex relative">
          <input 
            v-model="postcode" 
            @input="handlePostcodeInput"
            @focus="showSuggestions = true"
            class="mobile-input bg-gray-200 border-2 border-blue-300 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-lg" 
            id="postcode" 
            type="text" 
            placeholder="Enter your Postcode or Address..." 
            autocomplete="off"
          />
          <button 
            v-if="!isLoadingPostcode && postcode.trim()"
            class="submit-btn bg-green-500 hover:bg-green-600 text-white px-6 rounded-full font-bold transition duration-300 ease-in-out transform hover:scale-105" 
            type="button" 
            @click="validatePostcode"
          >GO</button>
          <div v-if="isLoadingPostcode" class="submit-btn bg-green-500 text-white px-6 rounded-full font-bold flex items-center">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
        </div>

        <!-- Postcode Error -->
        <div v-if="postcodeError" class="text-red-500 text-sm mt-2">{{ postcodeError }}</div>

        <!-- Address Suggestions Dropdown -->
        <div v-if="showSuggestions && addressSuggestions.length > 0" class="relative mt-2 z-20" v-click-outside="closeSuggestions">
          <ul class="absolute top-0 left-0 right-0 bg-white rounded shadow-lg text-gray-700 max-h-64 overflow-y-auto border border-gray-200">
            <li 
              v-for="(suggestion, index) in addressSuggestions" 
              :key="index"
              @click="selectAddress(suggestion)"
              class="py-2 px-3 hover:bg-green-50 cursor-pointer border-b border-gray-100 text-sm"
            >
              <div class="font-medium text-gray-800">{{ suggestion.address || suggestion.postcode || 'Address' }}</div>
              <div class="text-xs text-gray-500">{{ suggestion.postcode }}</div>
            </li>
          </ul>
        </div>

        <!-- Manual Entry Fallback Option (shown if API has failed) -->
        <div v-if="apiFailedMessage" class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p class="text-sm text-yellow-800 mb-2">{{ apiFailedMessage }}</p>
          <button 
            type="button" 
            @click="showManualEntry = true"
            class="text-yellow-600 underline text-sm font-semibold hover:text-yellow-700"
          >
            Enter Address Manually →
          </button>
        </div>

        <!-- No Results Message -->
        <div v-if="showSuggestions && postcode.trim() && addressSuggestions.length === 0 && !isLoadingPostcode && !apiFailedMessage" class="text-gray-500 text-sm mt-2">
          No addresses found. Try checking your postcode or use manual entry.
        </div>
      </div>

      <!-- STEP 1B: Manual Address Entry Fallback -->
      <div v-if="showManualEntry && !addressSelected" class="mb-4">
        <div class="bg-yellow-50 border-l-4 border-yellow-500 p-3 mb-4">
          <p class="text-yellow-700 font-semibold text-sm">Manual Address Entry</p>
          <button type="button" @click="backToAutocomplete" class="text-yellow-600 underline text-sm mt-1 hover:text-yellow-700">
            ← Back to Autocomplete
          </button>
        </div>

        <label class="block text-gray-700 font-bold mb-2 text-lg" for="street-address">
          Street Address
        </label>
        <input v-model="manualStreetAddress" class="mobile-input bg-gray-200 border-2 border-blue-300 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-lg" id="street-address" type="text" placeholder="Enter your street address..." />
        <div v-if="manualStreetAddressError" class="text-red-500 text-sm mt-2">{{ manualStreetAddressError }}</div>

        <label class="block text-gray-700 font-bold mb-2 text-lg mt-4" for="city">
          City/Town
        </label>
        <input v-model="manualCity" class="mobile-input bg-gray-200 border-2 border-blue-300 w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none focus:border-blue-500 text-lg" id="city" type="text" placeholder="Enter your city or town..." />
        <div v-if="manualCityError" class="text-red-500 text-sm mt-2">{{ manualCityError }}</div>

        <button 
          class="submit-btn w-full bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-bold transition duration-300 ease-in-out transform hover:scale-105 mt-6" 
          type="button"
          @click="submitManualAddress"
        >
          Continue <i class="fas fa-arrow-right ml-2"></i>
        </button>
      </div>

      <!-- STEP 2: Selected Address Confirmation -->
      <div v-if="addressSelected" class="mb-4">
        <div class="bg-green-50 border-l-4 border-green-500 p-3 mb-4">
          <p class="text-green-700 font-semibold">Address Selected</p>
          <p class="text-gray-700 text-sm mt-1">{{ selectedAddressDisplay }}</p>
          <button type="button" @click="changeAddress" class="text-green-600 underline text-sm mt-2 hover:text-green-700">
            Change Address
          </button>
        </div>

        <button class="submit-btn w-full bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-bold transition duration-300 ease-in-out transform hover:scale-105 mt-6" type="submit">
          Continue <i class="fas fa-arrow-right ml-2"></i>
        </button>
      </div>

      <!-- Review Section -->
      <div class="border-t-2 border-gray-200 mt-4 py-4">
        <div class="flex justify-start items-start">
          <div class="rounded-full h-14 w-14 overflow-hidden mr-4">
            <img src="@/assets/review-photo.png" alt="Person" class="h-full w-full object-cover" />
          </div>
          <div>
            <p class="text-gray-700 text-sm italic">"I got a great offer for my property using this service. Highly recommended!"</p>
            <div class="text-yellow-400 text-xl mt-2">
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <i class="fa fa-star"></i>
              <img src="/img/logo3.99f58573.png" alt="Google Logo" class="ml-2" style="height: 24px;">
            </div>
            <p class="text-gray-500 text-sm mt-2">- Recent Seller, Preston</p>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { postcodeService } from "../services/postcodeService";

export default {
  directives: {
    clickOutside: {
      bind(el, binding, vnode) {
        el.clickOutsideHandler = function(event) {
          if (!el.contains(event.target)) {
            vnode.context[binding.expression](event);
          }
        };
        document.addEventListener('click', el.clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideHandler);
      },
    },
  },
  data() {
    return {
      postcode: "",
      addressSuggestions: [],
      showSuggestions: false,
      isLoadingPostcode: false,
      selectedAddress: null,
      addressSelected: false,
      submitted: false,
      postcodeError: "",
      debounceTimer: null,
      searchType: "postcode",
      showManualEntry: false,
      apiFailedMessage: "",
      manualStreetAddress: "",
      manualCity: "",
      manualStreetAddressError: "",
      manualCityError: "",
    };
  },
  computed: {
    selectedAddressDisplay() {
      if (this.selectedAddress) {
        return `${this.selectedAddress.address}, ${this.selectedAddress.postcode}`;
      }
      return "";
    },
    reviewText() {
      if (this.$route.path.startsWith('/financial/')) {
        return "World was about to end, and lose my home, the fight in me made me not bury my head and made the call I had 48hrs till eviction and Adam was so calm. Made the whole process without stress and was a man of his word from start to end five-star people don't lose your home make the call";
      } else {
        return "The offer that was given did not change... I Was getting extremely frustrated going with companies that would give me a high sale price then drop it at the last minute. Luckily, I found Adam and rapid Sale who was honest from the start. The offer that was given did not change. All got complete very quickly with no hassle. If your after a quick sale be careful of a lot of companies that pop up first when searching and give Adam a call. Would highly recommend Adam and his team.";
      }
    },
    reviewerName() {
      if (this.$route.path.startsWith('/financial/')) {
        return "- Neil Whitehall";
      } else {
        return "By Mark Lynas";
      }
    }
  },
  methods: {
    handlePostcodeInput() {
      this.postcodeError = "";
      
      // Clear previous suggestions
      this.addressSuggestions = [];
      
      // Only search if input has at least 2 characters
      if (this.postcode.trim().length < 2) {
        this.showSuggestions = false;
        return;
      }

      // Debounce the search
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.searchAddresses();
      }, 300);
    },

    async searchAddresses() {
      this.isLoadingPostcode = true;
      this.postcodeError = "";
      this.apiFailedMessage = ""; // Clear previous failure message

      try {
        const query = this.postcode.trim();
        console.log('[AddressForm] searchAddresses called with query:', query);
        
        // Use the postcode service
        console.log('[AddressForm] Calling postcodeService.searchAddresses...');
        const response = await postcodeService.searchAddresses(query, 10);
        console.log("[AddressForm] Postcode API Response received:", response);

        if (response.result && response.result.hits && response.result.hits.length > 0) {
          console.log('[AddressForm] Found', response.result.hits.length, 'addresses');
          this.addressSuggestions = response.result.hits.map(hit => {
            // Handle both postcode and address response formats
            const address = hit.address || hit.suggestion || `${hit.postcode}`;
            
            // Extract postcode - try multiple sources
            // 1. hit.postcode directly
            // 2. Extract from the address string if it contains a UK postcode pattern
            // 3. Fall back to the user's input postcode
            let extractedPostcode = hit.postcode;
            
            if (!extractedPostcode && address) {
              // Try to extract UK postcode from address string (e.g., "PR2 1XL")
              const postcodeMatch = address.match(/[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}/i);
              if (postcodeMatch) {
                extractedPostcode = postcodeMatch[0].toUpperCase();
              }
            }
            
            // Fallback to user's input if still no postcode
            if (!extractedPostcode) {
              extractedPostcode = this.postcode.trim().toUpperCase();
            }
            
            console.log("[AddressForm] Processing hit:", hit, "Extracted address:", address, "Postcode:", extractedPostcode);
            
            return {
              address: address,
              postcode: extractedPostcode,
              uprn: hit.uprn,
              id: hit.id,
              country: hit.country || 'United Kingdom', // Use API country or default
              fullHit: hit, // Store full hit for debugging
            };
          }).sort((a, b) => {
            // Sort by house number at the start of the address
            const numA = parseInt(a.address.match(/^\d+/)?.[0] || '0', 10);
            const numB = parseInt(b.address.match(/^\d+/)?.[0] || '0', 10);
            return numA - numB;
          });
          this.showSuggestions = true;
          this.apiFailedMessage = ""; // Clear any previous error
          console.log("[AddressForm] Found addresses:", this.addressSuggestions);
        } else {
          console.log("[AddressForm] No results found. Response:", response);
          this.addressSuggestions = [];
          this.showSuggestions = false;
          this.postcodeError = "No addresses found. Try a different postcode.";
        }
      } catch (error) {
        console.error("[AddressForm] Error fetching addresses:", error.message);
        console.error("[AddressForm] Error type:", error.name);
        console.error("[AddressForm] Full error:", error);
        
        // Set API failure message to trigger manual entry option
        this.apiFailedMessage = "Address lookup service is temporarily unavailable. You can enter your address manually below.";
        this.postcodeError = "";
        this.addressSuggestions = [];
        this.showSuggestions = false;
      } finally {
        this.isLoadingPostcode = false;
      }
    },

    async selectAddress(suggestion) {
      console.log("[AddressForm] Selected address:", suggestion);
      
      // Ensure we have a valid address display
      const displayAddress = suggestion.address || suggestion.postcode || 'Unknown Address';
      console.log("[AddressForm] Display address:", displayAddress);
      
      // Start with the suggestion data
      let country = suggestion.country || 'United Kingdom';
      
      // If we have a UDPRN, resolve the full address to get country
      if (suggestion.uprn || suggestion.fullHit?.udprn) {
        const udprn = suggestion.fullHit?.udprn || suggestion.uprn;
        console.log("[AddressForm] Resolving address with UDPRN:", udprn);
        
        try {
          const resolved = await postcodeService.resolveAddress(udprn);
          if (resolved && resolved.result) {
            country = resolved.result.country || country;
            console.log("[AddressForm] Resolved country:", country);
          }
        } catch (error) {
          console.warn("[AddressForm] Could not resolve address details:", error.message);
        }
      }
      
      this.selectedAddress = {
        ...suggestion,
        address: displayAddress, // Ensure address is always set
        country: country,
      };
      
      console.log("[AddressForm] Final selected address with country:", this.selectedAddress);
      
      this.addressSelected = true;
      this.showSuggestions = false;
      this.postcode = suggestion.postcode;
      this.postcodeError = "";
      this.submitted = false;
    },

    changeAddress() {
      this.addressSelected = false;
      this.selectedAddress = null;
      this.postcode = "";
      this.addressSuggestions = [];
      this.showSuggestions = false;
      this.postcodeError = "";
      this.submitted = false;
    },

    validatePostcode() {
      this.postcodeError = "";
      
      if (!this.postcode.trim()) {
        this.postcodeError = "Please enter a postcode or address";
        return;
      }

      // If we already have suggestions shown, just take the first one
      if (this.addressSuggestions.length > 0) {
        this.selectAddress(this.addressSuggestions[0]);
      } else {
        this.postcodeError = "No addresses found. Please select from the dropdown or try again.";
      }
    },

    handleSubmit() {
      this.submitted = true;

      if (!this.selectedAddress) {
        this.postcodeError = "Please select an address";
        return;
      }

      // Parse the address to extract street and locality
      const addressParts = this.selectedAddress.address.split(',').map(part => part.trim());
      const street = addressParts.slice(0, -1).join(', ') || this.selectedAddress.address;
      
      // Use city from manual entry if available, otherwise extract from address
      const city = this.selectedAddress.city || addressParts[addressParts.length - 1] || '';

      // All validation passed - emit the address data
      const addressData = {
        street: street,
        localityTownCounty: city,
        postcode: this.selectedAddress.postcode,
        country: this.selectedAddress.country || "United Kingdom",
      };

      console.log("[AddressForm] Address submitted:", addressData);
      console.log("[AddressForm] Postcode being sent:", addressData.postcode);
      this.$emit("addressSelected", addressData);

      // Proceed to quiz
      this.proceedToQuiz();
    },

    proceedToQuiz() {
      this.$emit('proceedToQuiz');
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    },

    closeSuggestions() {
      this.showSuggestions = false;
    },

    backToAutocomplete() {
      this.showManualEntry = false;
      this.manualStreetAddress = "";
      this.manualCity = "";
      this.manualStreetAddressError = "";
      this.manualCityError = "";
    },

    submitManualAddress() {
      this.manualStreetAddressError = "";
      this.manualCityError = "";

      // Validate
      if (!this.manualStreetAddress.trim()) {
        this.manualStreetAddressError = "Please enter your street address";
        return;
      }

      if (!this.manualCity.trim()) {
        this.manualCityError = "Please enter your city or town";
        return;
      }

      // Create a fake suggestion object for manual entry
      const manualAddress = {
        address: this.manualStreetAddress.trim(),
        postcode: this.postcode.trim(),
        city: this.manualCity.trim(),
        country: "United Kingdom", // Default for manual entry
        id: "manual-entry",
      };

      this.selectAddress(manualAddress);
    },
  },
};
</script>

<style scoped>
/* Base styles for the form container */
.w-full {
  max-width: 100%;
}

/* Padding for better spacing on all devices */
.py-6 {
  padding: 2rem 1rem;
}

/* Form header styles */
.bg-green-500 {
  background-color: #38a169;
}

.text-white {
  color: white;
}

/* Postcode input and button styles */
.mobile-input,
.submit-btn {
  padding: 0.5rem 1rem;
  font-size: 1rem;
}

/* Button-specific styles */
.submit-btn {
  background-color: #48bb78;
  color: white;
  margin-left: 0.5rem;
}

/* Review section styles */
.review-section {
  background-color: #f7f7f7;
  border-radius: 10px;
  padding: 2rem;
  margin-top: 2rem;
  border-top: 2px solid #e2e8f0;
  position: relative;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Review text styles */
.review-text {
  font-style: italic;
  color: #4a5568;
  line-height: 1.6;
  max-width: 925px;
  margin: auto;
}

/* Star rating styles */
.star-rating {
  font-size: 1.25rem;
  color: #ecc94b;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  margin-top: 1rem;
}

.reviewer-info {
  font-size: 0.9rem;
  color: #718096;
  text-align: right;
  position: relative;
  z-index: 1;
}

.tooltip .tooltip-text {
  position: absolute;
  z-index: 10;
}

.tooltip:hover .tooltip-text {
  display: block;
}

/* Address confirmation box */
.bg-green-50 {
  background-color: #f0fdf4;
}

.border-l-4 {
  border-left-width: 4px;
}

.border-green-500 {
  border-left-color: #22c55e;
}

/* Autocomplete dropdown styles */
.z-20 {
  z-index: 20;
}

/* Media query for mobile devices */
@media only screen and (max-width: 600px) {
  .w-full.max-w-xs.sm\:max-w-md.mx-auto.py-2.px-4.rounded-lg {
    padding: 1rem;
  }

  .mobile-input {
    flex-grow: 1;
    max-width: calc(100% - 32px);
  }

  /* Only apply small width to the GO button (not full-width Continue buttons) */
  .submit-btn:not(.w-full) {
    flex-shrink: 0;
    width: 50px;
    padding: 0.5rem 0.25rem;
  }

  .review-text,
  .reviewer-info,
  .star-rating i {
    font-size: 0.775rem;
  }
}
</style>
